import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../utils/api';
import { Heart, MessageCircle, ArrowLeft, Share2, Send } from 'lucide-react';
import { API } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/common/BottomNav';
import TopHeader from '../components/common/TopHeader';
import { formatDate, TEAL_ACCENT } from '../utils/homeHelpers';
import notify from '../utils/notify';
import MentionInput from '../components/shared/MentionInput';
import renderCommentContent from '../utils/renderCommentContent';
import FeedPhoto from '../components/shared/FeedPhoto';

const Shell = ({ children }) => (
  <div style={{ background: '#F5F7FA', minHeight: '100vh', paddingBottom: 80 }}>
    <TopHeader />
    <div style={{ width: '100%', maxWidth: 380, margin: '0 auto', padding: '0 16px' }}>
      {children}
    </div>
    <BottomNav active="home" />
  </div>
);

const BackBar = ({ navigate }) => (
  <button
    onClick={() => navigate('/')}
    data-testid="post-detail-back"
    style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '12px 0',
      background: 'transparent', border: 'none', color: TEAL_ACCENT,
      fontSize: 14, fontWeight: 600, cursor: 'pointer',
    }}
  >
    <ArrowLeft size={16} /> Back to feed
  </button>
);

const initialsOf = (name) =>
  (name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

const CommentItem = ({ comment }) => (
  <div
    data-testid={`post-detail-comment-${comment.id}`}
    style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}
  >
    <div style={{
      width: 32, height: 32, borderRadius: '50%', background: '#F3F4F6',
      border: `2px solid ${TEAL_ACCENT}30`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: TEAL_ACCENT, fontWeight: 600, fontSize: 11,
      flexShrink: 0,
    }}>
      {initialsOf(comment.username)}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
          {comment.username || 'Team Member'}
        </span>
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>{formatDate(comment.created_at)}</span>
      </div>
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap' }}>
        {renderCommentContent(comment.content, comment.mentions)}
      </div>
    </div>
  </div>
);

const PostDetailPage = () => {
  const { postId: rawPostId } = useParams();
  // Strip the carousel-wrapper prefix that some legacy share URLs include
  // (`/posts/post_<uuid>`) so we always hit the backend with a clean UUID.
  const postId = (rawPostId || '').replace(/^post_/, '');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [liked, setLiked] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentMentions, setCommentMentions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setErrorMsg('');
      try {
        const res = await axios.get(`${API}/posts/${postId}`);
        if (cancelled) return;
        setPost(res.data);
        setComments(Array.isArray(res.data?.comments) ? res.data.comments : []);
        setLiked(Array.isArray(res.data?.likes) && !!user && res.data.likes.includes(user.id));
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setErrorMsg(err?.response?.data?.detail || 'Unable to load this post. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (postId) load();
    return () => { cancelled = true; };
  }, [postId, user]);

  const handleLike = async () => {
    if (!post || likeBusy) return;
    setLikeBusy(true);
    try {
      const r = await axios.post(`${API}/posts/${post.id}/like`);
      setLiked(!!r.data?.liked);
      setPost(prev => prev ? { ...prev, likes_count: r.data?.total_likes ?? prev.likes_count } : prev);
    } catch {
      // best-effort UX action; ignore errors silently
    } finally {
      setLikeBusy(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = { title: 'Check out this post on SocialStar!', text: post?.content || '', url };
    try {
      if (navigator.share) await navigator.share(shareData);
      else { await navigator.clipboard.writeText(url); notify.success('Link copied to clipboard'); }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        try { await navigator.clipboard.writeText(url); notify.success('Link copied'); } catch { /* ignore */ }
      }
    }
  };

  const handleSubmitComment = async (e) => {
    e?.preventDefault?.();
    const text = commentText.trim();
    if (!text || !post || submitting) return;
    setSubmitting(true);
    try {
      // POST /api/posts/{post_id}/comment — auth header attached by the
      // shared axios instance configured in utils/api.js.
      const res = await axios.post(`${API}/posts/${post.id}/comment`, { content: text, mentions: commentMentions });
      const newComment = res.data;
      setComments(prev => [...prev, newComment]);
      setPost(prev => prev ? { ...prev, comments_count: (prev.comments_count || 0) + 1 } : prev);
      setCommentText(''); setCommentMentions([]);
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <BackBar navigate={navigate} />
        <div data-testid="post-detail-loading" style={{ padding: '40px 16px', textAlign: 'center', color: '#6B7280' }}>
          Loading post…
        </div>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell>
        <BackBar navigate={navigate} />
        <div
          data-testid="post-detail-not-found"
          style={{
            background: '#FFFFFF', borderRadius: 16, padding: 28, marginTop: 12,
            textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', marginBottom: 8 }}>
            Post not found
          </div>
          <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5, marginBottom: 20 }}>
            This post may have been removed or it belongs to a different organisation.
          </div>
          <button
            onClick={() => navigate('/')}
            data-testid="post-detail-go-home"
            style={{
              padding: '12px 22px', background: TEAL_ACCENT, color: '#FFFFFF',
              border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Go to home feed
          </button>
        </div>
      </Shell>
    );
  }

  if (errorMsg) {
    return (
      <Shell>
        <BackBar navigate={navigate} />
        <div
          data-testid="post-detail-error"
          style={{
            background: '#FEF2F2', borderRadius: 12, padding: 16, marginTop: 12,
            color: '#991B1B', fontSize: 14,
          }}
        >
          {errorMsg}
        </div>
      </Shell>
    );
  }

  if (!post) return <Shell><BackBar navigate={navigate} /></Shell>;

  const imageUrl = post.media_url
    ? (post.media_url.startsWith('/api') ? `${API.replace('/api', '')}${post.media_url}` : post.media_url)
    : null;
  const likesTotal = post.likes_count ?? (post.likes?.length || 0);
  const commentsTotal = post.comments_count ?? comments.length;

  return (
    <Shell>
      <BackBar navigate={navigate} />

      <article
        data-testid="post-detail-card"
        style={{
          background: '#FFFFFF', borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: TEAL_ACCENT, textTransform: 'uppercase', letterSpacing: '0.5px' }}>User Post</span>
        </div>

        {/* Author */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px 8px' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: '#F3F4F6',
            border: `2px solid ${TEAL_ACCENT}30`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: TEAL_ACCENT, fontWeight: 600, fontSize: 14,
          }}>
            {initialsOf(post.username)}
          </div>
          <div style={{ flex: 1 }}>
            <div data-testid="post-detail-author" style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
              {post.username || 'Team Member'}
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{formatDate(post.created_at)}</div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '4px 16px 12px' }}>
          <p data-testid="post-detail-content" style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
            {post.content}
          </p>
          {Array.isArray(post.tags) && post.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {post.tags.map((tag, i) => (
                <span key={i} style={{ color: TEAL_ACCENT, fontSize: 12, fontWeight: 500 }}>
                  #{String(tag).replace(/^#/, '')}
                </span>
              ))}
            </div>
          )}
        </div>

        {imageUrl && (
          <FeedPhoto src={imageUrl} alt="Post photo" testId="post-detail-image" />
        )}

        {/* Footer actions */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 18,
          padding: '12px 16px', borderTop: '1px solid rgba(0,0,0,0.05)',
        }}>
          <button
            onClick={handleLike}
            disabled={likeBusy}
            data-testid="post-detail-like-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6, background: 'transparent',
              border: 'none', padding: 0, cursor: likeBusy ? 'default' : 'pointer',
              color: liked ? '#DC2626' : '#6B7280', fontSize: 13, fontWeight: 500,
            }}
          >
            <Heart size={18} fill={liked ? '#DC2626' : 'none'} />
            <span data-testid="post-detail-likes-count">{likesTotal}</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 13, fontWeight: 500 }}>
            <MessageCircle size={18} />
            <span data-testid="post-detail-comments-count">{commentsTotal}</span>
          </div>
          <button
            onClick={handleShare}
            data-testid="post-detail-share-btn"
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              color: '#6B7280', fontSize: 13, fontWeight: 500,
            }}
          >
            <Share2 size={18} /> Share
          </button>
        </div>
      </article>

      {/* Comments section */}
      <section
        data-testid="post-detail-comments-section"
        style={{
          background: '#FFFFFF', borderRadius: 16, padding: '12px 16px', marginTop: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', padding: '4px 0 8px' }}>
          Comments ({commentsTotal})
        </div>

        <div data-testid="post-detail-comments-list">
          {comments.length === 0 ? (
            <div style={{ padding: '12px 0', fontSize: 13, color: '#9CA3AF' }}>
              Be the first to comment.
            </div>
          ) : (
            comments.map((c) => <CommentItem key={c.id} comment={c} />)
          )}
        </div>

        <form
          onSubmit={handleSubmitComment}
          style={{ display: 'flex', gap: 8, alignItems: 'flex-end', paddingTop: 12 }}
        >
          <div style={{ flex: 1 }}>
            <MentionInput
              value={commentText}
              mentions={commentMentions}
              onChange={(text, ms) => { setCommentText(text); setCommentMentions(ms); }}
              placeholder="Write a comment… type @ to mention"
              rows={2}
              disabled={submitting}
              testId="post-detail-comment-input"
              style={{ background: '#F9FAFB' }}
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !commentText.trim()}
            data-testid="post-detail-comment-submit"
            style={{
              padding: '10px 12px', borderRadius: 10, border: 'none',
              background: TEAL_ACCENT, color: '#FFFFFF', cursor: (submitting || !commentText.trim()) ? 'default' : 'pointer',
              opacity: (submitting || !commentText.trim()) ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
            }}
          >
            <Send size={14} /> {submitting ? 'Posting…' : 'Post'}
          </button>
        </form>
      </section>
    </Shell>
  );
};

export default PostDetailPage;
